import {captureException} from '@sentry/browser'
import {DateTime} from 'luxon'
import Image from 'next/image'
import {FC, useCallback, useEffect, useRef, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Modal} from '@/components/common/modal/Modal'
import {getContactContent} from '@/content/contact'
import {Link, useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'
import {trackClickOnContactEmail} from '@/utils/tracking/helpers'

import {Text, TextFontWeight, TextVariant} from '../content/Text'

const LOCAL_STORAGE_KEY = 'closingPopup'

export const BeforeCloseModal: FC<React.PropsWithChildren<unknown>> = () => {
  const {t} = useTranslation('page')
  const {
    beforeCloseModal: {title, subtitle, imageSrc, imageAlt},
    consultationLinkHref,
    consultationLinkText,
    emailAddress,
    emailAddressHref,
  } = getContactContent()

  const [isOpen, setIsOpen] = useState(false)
  const handleClose = () => setIsOpen(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // when user leaves the window and returns back in time (currently 0.7s)
  const resetTimeout = () => {
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef.current)
      localStorage.removeItem(LOCAL_STORAGE_KEY)
    }
  }

  // when user leaves the window and does not return back in time (currently 0.7s)
  const handleTimeout = () => {
    setIsOpen(true)
    timeoutRef.current = null
  }

  const handleMouseLeave = useCallback((e: MouseEvent) => {
    if (e.clientY < document.documentElement.clientHeight / 20) {
      try {
        // wait 24 hours before showing again
        const lastVisible = localStorage.getItem(LOCAL_STORAGE_KEY)
        if (!lastVisible || DateTime.now().minus({day: 1}) > DateTime.fromISO(lastVisible)) {
          timeoutRef.current = setTimeout(handleTimeout, 1000)
        }
        localStorage.setItem(LOCAL_STORAGE_KEY, new Date().toISOString())
        analytics.track('Before Close Popup Viewed')
      } catch (e) {
        captureException(e)
      }
    }
  }, [])

  useEffect(() => {
    document.body.addEventListener('mouseleave', handleMouseLeave)
    document.body.addEventListener('mouseenter', resetTimeout)

    return () => {
      document.body.removeEventListener('mouseleave', handleMouseLeave)
      document.body.removeEventListener('mouseenter', resetTimeout)
    }
  }, [handleMouseLeave])

  return (
    <>
      <Modal isVisible={isOpen} closeModal={handleClose} hasPadding={false}>
        <div className="content">
          <Text
            variant={TextVariant.h5}
            element="p"
            color={theme.palette.text.primary}
            align="center"
          >
            {t(title)}
          </Text>
          <div className="subtitle">
            <Text variant={TextVariant.h4} element="h2" align="center">
              {t(subtitle)}
            </Text>
          </div>
          <div className="wrapper">
            <div className="image">
              <Image
                src={imageSrc}
                alt={imageAlt}
                style={{objectFit: 'contain'}}
                width={180}
                height={120}
              />
            </div>
            <div className="contact">
              <Text
                fontWeight={TextFontWeight.bold}
                align="center"
                color={theme.palette.text.primary}
              >
                <a
                  href={emailAddressHref}
                  className="email"
                  onClick={() => trackClickOnContactEmail({position: 'Before close modal'})}
                >
                  {emailAddress}
                </a>
              </Text>
              <Link href={consultationLinkHref} legacyBehavior>
                <a target="_blank" rel="noopener noreferrer">
                  <Button
                    type="button"
                    variant={ButtonVariant.primary}
                    size={ButtonSize.small}
                    fullWidth
                  >
                    {t(consultationLinkText)}
                  </Button>
                </a>
              </Link>
            </div>
          </div>
        </div>
      </Modal>

      <style jsx>{`
        .content {
          padding: ${theme.spacing[6]};
          background-color: ${theme.palette.background.butter};
          border-radius: 16px;
        }

        .subtitle {
          margin-top: ${theme.spacing[2]};
        }

        .wrapper {
          display: flex;
          align-items: center;
          margin-top: ${theme.spacing[3]};
        }

        .image {
          position: relative;
          width: 180px;
          height: 120px;
          margin-right: ${theme.spacing[5]};
        }

        .email {
          display: block;
          color: inherit;
          margin-bottom: ${theme.spacing[3]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .wrapper {
            justify-content: center;
          }
          .image {
            display: none;
          }
        }
      `}</style>
    </>
  )
}
